import React, { useEffect, useState, useRef } from 'react';
import { Table, Input, Row, Col, Select, Typography, Space, Card, Flex, Button, Modal, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import api from '../../API';
import { Link, useNavigate } from 'react-router-dom';

function OASubAccountList() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailOptions, setEmailOptions] = useState([]);
  const [relativeData, setRelativeData] = useState({
      originalOAAccounts: [],
      originalSubUsers: []
  })
  const originalTableData = useRef([]);

  const columns = [
    {
      title: 'Sub-user (email)',
      dataIndex: 'email',
      render: (text) => renderHighlightedText(text)
    },
    {
      title: 'OA Name',
      dataIndex: 'accountName',
      render: (text) => renderHighlightedText(text)
    },
    {
      title: 'OA ID',
      dataIndex: 'OAId',
      render: (text) => renderHighlightedText(text)
    },
    {
      title: 'Action',
      key: 'action',
      width: 105,
      align: 'center',
      render: (_, record) => (
      <Flex justify="space-around" align="center">
        <Button 
            type="primary" 
            icon={<EditOutlined/>} 
            onClick={() => navigate(
                `/oasubaccounts/edit`,
                {state: [
                  originalTableData.current.find(obj => obj.key === record.key),
                  relativeData.originalOAAccounts,
                  relativeData.originalSubUsers
                ]}
            )}>
        </Button>
        <Button 
            type="primary" danger 
            icon={<DeleteOutlined/>}
            onClick={() => {
            Modal.confirm({
                title: 'Confirm deletion',
                content: `Are you sure you want to delete this OA account?`,
                onOk() {handleRemoveRow(record.key, record.OAId)},
                maskClosable: true
            })}}></Button>
      </Flex>),
  }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [oaaccounts, oasofsubuser, subusers] = await Promise.all([
        api.list_oaaccounts_by_user(user?.userId, user?.parentId, ["id", "accountName", "OAId"]),
        api.list_oaaccounts_ofSubusers(user?.userId),
        api.get_subuser_byParentId(user?.userId,["userId","email"])
      ])
      setRelativeData((prevObj) => ({
        ...prevObj,
        originalOAAccounts: oaaccounts,
        originalSubUsers: subusers
      }))
      originalTableData.current = oasofsubuser.map(oaofsubuser => ({
        key: oaofsubuser.id,
        email: oaofsubuser?.email,
        accountName: oaofsubuser?.accountName,
        OAId: oaofsubuser.OAId
      }));
      setDataSource(originalTableData.current);

      // Get unique emails for filter
      const uniqueEmails = [...new Set(originalTableData.current.map(item => item.email))];
      setEmailOptions(uniqueEmails.map(email => ({ value: email, label: email })));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderHighlightedText = (text) => {
    return searchText ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text ? text.toString() : ''}
      />
    ) : (
      text
    );
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    filterData(value, selectedEmail);
  };

  const handleEmailFilter = (value) => {
    setSelectedEmail(value);
    filterData(searchText, value);
  };

  const filterData = (search, email) => {
    let filteredData = [...originalTableData.current];

    if (email) {
      filteredData = filteredData.filter(item => item.email === email);
    }

    if (search) {
      filteredData = filteredData.filter(item => 
        Object.keys(item).slice(1).some(key => 
          item[key] && item[key].toString().toLowerCase().includes(search.toLowerCase())
        )
      );
    }

    setDataSource(filteredData);
  };

  async function handleRemoveRow(key) {
    try {
        setLoading(true);
        const response = await api.delete_oasubaccount_byId(key);
        message.success(response.message);
        originalTableData.current = originalTableData.current.filter(record => record.key !== key);
        setDataSource(originalTableData.current);
    } catch (error) {
        message.error(error.message);
    } finally {
        setLoading(false);
    }
  }

  return (
    <Card 
      style={{height: '100%', margin: "0 auto", boxShadow: "1px 1px 5px #ccc", borderRadius: 10 }}
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
            OA Sub Accounts
          </Typography.Title>
      }
      loading={loading}
      extra={ !user?.parentId &&
        <Button 
          type="primary" 
          style={{padding:15}} 
          icon={<PlusCircleOutlined/>}
          onClick={() => navigate(`/oasubaccounts/new`, {
            state: [
              null,
              relativeData.originalOAAccounts,
              relativeData.originalSubUsers
            ]
          })}
        >
            New Sub OA
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
          <Input
            placeholder="Search..."
            allowClear
            onChange={handleSearch}
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
          <Select
            style={{ width: '100%' }}
            placeholder="Filter by email"
            allowClear
            options={emailOptions}
            onChange={handleEmailFilter}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Table
            bordered
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            rowKey={(record) => `${record.email}-${record.OAId}`}
            scroll={{ x: 1000 }}
            pagination={{
              position: ["bottomCenter"],
              showSizeChanger: false, // Disable page size changer
              pageSize: 10, 
              showTotal: () => `Total: ${dataSource.length} items`
            }}
          />
        </Col>
      </Row>
    </Card>
  );
}

export default OASubAccountList;
